/**
 * Gantt view of the Tasks tool: sections table on the left, day/week timeline on the
 * right, bars you can drag to reschedule, milestones and dependency arrows.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Task } from '@/client/api/tasks/tasks';
import { useUpdateTaskMutation } from '@/client/api/tasks/tasks';
import { Avatar, STATUS, groupBySection, isClosed, personName, todayIso } from './taskUi';
import type { SectionGroup, TaskViewProps } from './taskUi';

const ROW = 36;
const LEFT = 460;
const HEAD = 48;
const BAR_H = 14;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

type Mode = 'days' | 'weeks';
const DAY_W: Record<Mode, number> = { days: 28, weeks: 12 };

const DAY_MS = 86_400_000;
/** YYYY-MM-DD -> whole day number (UTC, so DST never shifts it). */
const dn = (iso: string) => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;
const isoOf = (n: number) => new Date(n * DAY_MS).toISOString().slice(0, 10);
const md = (n: number) => {
  const d = new Date(n * DAY_MS);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

type Row = { k: 'sec'; g: SectionGroup; min: number | null; max: number | null } | { k: 'task'; t: Task } | { k: 'add'; g: SectionGroup };
interface Span {
  s: number;
  e: number;
}
interface Drag {
  id: string;
  mode: 'move' | 'resize';
  x: number;
  span: Span;
  moved: boolean;
}

const gkey = (g: SectionGroup) => g.id ?? 'none';

export default function GanttView(props: TaskViewProps) {
  const { tasks, loading, meta, project, selectedId, onOpen, onCreate } = props;
  const people = meta?.people;
  const update = useUpdateTaskMutation();

  const [mode, setMode] = useState<Mode>('days');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<{ key: string; milestone: boolean } | null>(null);
  const [over, setOver] = useState<Record<string, Span>>({});
  const drag = useRef<Drag | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dw = DAY_W[mode];

  // A fresh task list means the server has the new dates: drop the local overrides.
  useEffect(() => setOver({}), [tasks]);

  /** The task's dates as day numbers: dropped position first, else what the server has. */
  const spanOf = (t: Task): Span | null => {
    if (over[t.id]) return over[t.id];
    const a = t.startDate ? dn(t.startDate) : null;
    const b = t.dueDate ? dn(t.dueDate) : null;
    if (a == null && b == null) return null;
    if (t.isMilestone) return { s: (b ?? a)!, e: (b ?? a)! };
    const s = a ?? b!;
    const e = b ?? a!;
    return { s: Math.min(s, e), e: Math.max(s, e) };
  };

  const hasDates = useMemo(() => tasks.some((t) => t.startDate || t.dueDate), [tasks]);

  // Rows: section header, its tasks (unless collapsed), add footer.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    groupBySection(tasks, project).forEach((g) => {
      let min: number | null = null;
      let max: number | null = null;
      g.tasks.forEach((t) => {
        for (const iso of [t.startDate, t.dueDate]) {
          if (!iso) continue;
          const n = dn(iso);
          if (min == null || n < min) min = n;
          if (max == null || n > max) max = n;
        }
      });
      out.push({ k: 'sec', g, min, max });
      if (!collapsed.has(gkey(g))) {
        g.tasks.forEach((t) => out.push({ k: 'task', t }));
        out.push({ k: 'add', g });
      }
    });
    return out;
  }, [tasks, project, collapsed]);

  // Timeline range and per-day header cells.
  const { origin, count, today, days, months } = useMemo(() => {
    const today = dn(todayIso());
    let lo = today;
    let hi = today;
    tasks.forEach((t) => {
      for (const iso of [t.startDate, t.dueDate]) {
        if (!iso) continue;
        const n = dn(iso);
        lo = Math.min(lo, n);
        hi = Math.max(hi, n);
      }
    });
    const origin = lo - 3;
    const count = Math.max(hi + 14 - origin + 1, 42);
    const days: { x: number; date: number; dow: number }[] = [];
    const months: { x: number; w: number; label: string }[] = [];
    for (let i = 0; i < count; i++) {
      const d = new Date((origin + i) * DAY_MS);
      days.push({ x: i, date: d.getUTCDate(), dow: d.getUTCDay() });
      const label = `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
      const last = months[months.length - 1];
      if (last && last.label === label) last.w += 1;
      else months.push({ x: i, w: 1, label });
    }
    return { origin, count, today, days, months };
  }, [tasks]);

  const chartW = count * dw;
  const bodyH = rows.length * ROW;
  const xOf = (n: number) => (n - origin) * dw;

  /** Pixel rect of a task's bar (milestones: a 14px diamond centred on its day). */
  const rectOf = (t: Task, sp: Span) =>
    t.isMilestone ? { x: xOf(sp.s) + dw / 2 - 7, w: 14 } : { x: xOf(sp.s), w: (sp.e - sp.s + 1) * dw };

  // Scroll so today sits in the left third of the chart whenever the scale changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading) return;
    el.scrollLeft = Math.max(0, (today - origin) * dw - (el.clientWidth - LEFT) / 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, loading]);

  // Dependency connectors, from the end of each blocker's bar to the start of the dependent.
  const arrows = useMemo(() => {
    const pos = new Map<string, { x: number; w: number; y: number }>();
    rows.forEach((r, i) => {
      if (r.k !== 'task') return;
      const sp = spanOf(r.t);
      if (sp) pos.set(r.t.id, { ...rectOf(r.t, sp), y: i * ROW + ROW / 2 });
    });
    const paths: { key: string; d: string; tip: string }[] = [];
    pos.forEach((to, id) => {
      const t = tasks.find((x) => x.id === id);
      t?.dependsOn?.forEach((bid) => {
        const from = pos.get(bid);
        if (!from) return;
        const x1 = from.x + from.w;
        const y1 = from.y;
        const x2 = to.x;
        const y2 = to.y;
        let d: string;
        if (x2 >= x1 + 16) d = `M${x1} ${y1}H${x1 + 8}V${y2}H${x2 - 5}`;
        else {
          const ym = y1 + (y2 >= y1 ? 1 : -1) * (ROW / 2);
          d = `M${x1} ${y1}H${x1 + 8}V${ym}H${x2 - 8}V${y2}H${x2 - 5}`;
        }
        paths.push({ key: `${bid}>${id}`, d, tip: `${x2},${y2} ${x2 - 5},${y2 - 3} ${x2 - 5},${y2 + 3}` });
      });
    });
    return paths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tasks, over, dw, origin]);

  // --- dragging ---
  const onDown = (e: React.PointerEvent<HTMLDivElement>, t: Task, sp: Span) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const edge = (e.target as HTMLElement).dataset.edge === '1' && !t.isMilestone;
    drag.current = { id: t.id, mode: edge ? 'resize' : 'move', x: e.clientX, span: sp, moved: false };
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    if (Math.abs(e.clientX - d.x) > 3) d.moved = true;
    if (!d.moved) return;
    const dd = Math.round((e.clientX - d.x) / dw);
    const next = d.mode === 'move' ? { s: d.span.s + dd, e: d.span.e + dd } : { s: d.span.s, e: Math.max(d.span.s, d.span.e + dd) };
    setOver((o) => (o[d.id]?.s === next.s && o[d.id]?.e === next.e ? o : { ...o, [d.id]: next }));
  };
  const onUp = (e: React.PointerEvent<HTMLDivElement>, t: Task) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (!d.moved) return onOpen(t);
    const sp = over[d.id];
    if (!sp || (sp.s === d.span.s && sp.e === d.span.e)) return;
    if (t.isMilestone) update.mutate({ id: t.id, dueDate: isoOf(sp.e) });
    else update.mutate({ id: t.id, startDate: isoOf(sp.s), dueDate: isoOf(sp.e) });
  };

  const toggle = (key: string) =>
    setCollapsed((c) => {
      const n = new Set(c);
      if (!n.delete(key)) n.add(key);
      return n;
    });

  const submit = async (g: SectionGroup, title: string, milestone: boolean) => {
    const v = title.trim();
    setAdding(null);
    if (v) await onCreate({ title: v, sectionId: g.id, isMilestone: milestone });
  };

  const toolbar = (
    <div className="flex justify-end px-2 py-1.5">
      <div className="inline-flex overflow-hidden rounded-md border border-gray-200 text-sm">
        {(['days', 'weeks'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-3 py-1 capitalize ${mode === m ? 'bg-blue-50 font-medium text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {toolbar}
        <div className="border-t border-gray-200">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} style={{ height: ROW }} className="flex items-center gap-3 border-b border-gray-100 px-3">
              <div className="h-3 flex-1 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-12 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-12 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cell = 'shrink-0 truncate px-1 text-xs';

  return (
    <div className="flex h-full min-h-0 flex-col">
      {toolbar}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto border-t border-gray-200 bg-white">
        <div style={{ width: LEFT + chartW, minWidth: '100%' }}>
          {/* header */}
          <div className="sticky top-0 z-30 flex bg-white" style={{ height: HEAD }}>
            <div style={{ width: LEFT }} className="sticky left-0 z-40 flex shrink-0 items-end border-b border-r border-gray-200 bg-white pb-1.5 text-xs text-gray-500">
              <div className="min-w-0 flex-1 px-3">Tasks</div>
              <div className={`${cell} w-14`}>Start</div>
              <div className={`${cell} w-14`}>Finish</div>
              <div className={`${cell} w-28`}>Assigned to</div>
            </div>
            <div className="relative shrink-0 border-b border-gray-200 bg-white" style={{ width: chartW }}>
              {months.map((m) => (
                <div
                  key={m.label}
                  className="absolute top-0 truncate border-l border-gray-200 px-2 text-xs font-semibold leading-6 text-gray-700"
                  style={{ left: m.x * dw, width: m.w * dw, height: HEAD / 2 }}
                >
                  {m.label}
                </div>
              ))}
              {days.map((d) => {
                const isToday = origin + d.x === today;
                const label = mode === 'days' || d.dow === 1 ? d.date : '';
                return (
                  <div
                    key={d.x}
                    className={`absolute whitespace-nowrap text-center text-[11px] leading-6 ${
                      isToday ? 'bg-blue-100 font-bold text-blue-700' : d.dow === 0 || d.dow === 6 ? 'bg-gray-50 text-gray-500' : 'text-gray-600'
                    }`}
                    style={{ left: d.x * dw, width: dw, top: HEAD / 2, height: HEAD / 2 }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* body */}
          <div className="flex">
            {/* table */}
            <div style={{ width: LEFT }} className="sticky left-0 z-20 shrink-0 border-r border-gray-200 bg-white">
              {rows.map((r) => {
                if (r.k === 'sec') {
                  const key = gkey(r.g);
                  return (
                    <div key={`s${key}`} style={{ height: ROW }} className="flex items-center border-b border-gray-200 bg-gray-50 text-sm">
                      <button type="button" onClick={() => toggle(key)} className="flex min-w-0 flex-1 items-center gap-1.5 px-3 text-left">
                        <span className="w-3 text-[10px] text-gray-500">{collapsed.has(key) ? '▸' : '▾'}</span>
                        <span className="truncate font-semibold text-gray-900">{r.g.name}</span>
                        <span className="text-gray-400">{r.g.tasks.length}</span>
                      </button>
                      <div className={`${cell} w-14 text-gray-700`}>{r.min != null ? md(r.min) : ''}</div>
                      <div className={`${cell} w-14 text-gray-700`}>{r.max != null ? md(r.max) : ''}</div>
                      <div className="w-28 shrink-0" />
                    </div>
                  );
                }
                if (r.k === 'add') {
                  const key = gkey(r.g);
                  const a = adding?.key === key ? adding : null;
                  return (
                    <div key={`a${key}`} style={{ height: ROW }} className="flex items-center gap-1 border-b border-gray-100 px-3 text-sm">
                      {a ? (
                        <input
                          autoFocus
                          placeholder={a.milestone ? 'New milestone' : 'New task'}
                          className="h-7 w-full rounded border border-blue-300 px-2 text-sm outline-none focus:ring-1 focus:ring-blue-400"
                          onBlur={() => setAdding(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void submit(r.g, e.currentTarget.value, a.milestone);
                            else if (e.key === 'Escape') setAdding(null);
                          }}
                        />
                      ) : (
                        <>
                          <span className="text-blue-600">+</span>
                          <button type="button" className="text-blue-600 hover:underline" onClick={() => setAdding({ key, milestone: false })}>
                            New Task
                          </button>
                          <span className="text-gray-300">·</span>
                          <button type="button" className="text-blue-600 hover:underline" onClick={() => setAdding({ key, milestone: true })}>
                            New Milestone
                          </button>
                        </>
                      )}
                    </div>
                  );
                }
                const t = r.t;
                const sp = spanOf(t);
                const closed = isClosed(t.status);
                const live = !!over[t.id];
                return (
                  <div
                    key={t.id}
                    style={{ height: ROW }}
                    onClick={() => onOpen(t)}
                    className={`relative flex cursor-pointer items-center border-b border-gray-100 text-sm ${
                      t.id === selectedId ? 'bg-blue-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    {t.unread && <span className="absolute inset-y-0 left-0 w-[3px] bg-blue-500" />}
                    <div className={`min-w-0 flex-1 truncate pl-6 pr-2 ${closed ? 'text-gray-400 line-through' : 'text-gray-900'} ${t.unread ? 'font-semibold' : ''}`}>
                      {t.title}
                    </div>
                    <div className={`${cell} w-14 text-gray-700`}>{live && sp ? md(sp.s) : t.startDate ? md(dn(t.startDate)) : '-'}</div>
                    <div className={`${cell} w-14 text-gray-700`}>{live && sp ? md(sp.e) : t.dueDate ? md(dn(t.dueDate)) : '-'}</div>
                    <div className={`${cell} w-28 text-gray-700`}>{personName(people, t.ownerUid)}</div>
                  </div>
                );
              })}
            </div>

            {/* chart */}
            <div className="relative shrink-0" style={{ width: chartW, height: bodyH }}>
              {days.map((d) => {
                const isToday = origin + d.x === today;
                if (!isToday && d.dow !== 0 && d.dow !== 6) return null;
                return (
                  <div
                    key={d.x}
                    className={`pointer-events-none absolute inset-y-0 ${isToday ? 'bg-blue-100/70' : 'bg-gray-100/60'}`}
                    style={{ left: d.x * dw, width: dw }}
                  />
                );
              })}
              <div className="pointer-events-none absolute inset-y-0 w-px bg-blue-300" style={{ left: xOf(today) + dw / 2 }} />

              {rows.map((r, i) => {
                const top = i * ROW;
                if (r.k === 'sec') {
                  return (
                    <div key={`s${gkey(r.g)}`} className="absolute inset-x-0 border-b border-gray-200 bg-gray-50/50" style={{ top, height: ROW }}>
                      {r.min != null && r.max != null && (
                        <div
                          className="absolute rounded-sm bg-gray-500"
                          style={{ left: xOf(r.min), width: (r.max - r.min + 1) * dw, top: ROW / 2 - 3, height: 6 }}
                        />
                      )}
                    </div>
                  );
                }
                if (r.k === 'add') {
                  return <div key={`a${gkey(r.g)}`} className="absolute inset-x-0 border-b border-gray-100" style={{ top, height: ROW }} />;
                }
                const t = r.t;
                const sp = spanOf(t);
                const rowCls = `absolute inset-x-0 border-b border-gray-100 ${t.id === selectedId ? 'bg-blue-100/70' : ''}`;
                if (!sp) return <div key={t.id} className={rowCls} style={{ top, height: ROW }} />;
                const { x, w } = rectOf(t, sp);
                const live = !!over[t.id];
                const days = sp.e - sp.s + 1;
                const common = {
                  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => onDown(e, t, sp),
                  onPointerMove: onMove,
                  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => onUp(e, t),
                  onPointerCancel: () => (drag.current = null),
                };
                return (
                  <div key={t.id} className={rowCls} style={{ top, height: ROW, zIndex: live ? 20 : 10 }}>
                    {t.isMilestone ? (
                      <div
                        {...common}
                        title={t.title}
                        className="absolute h-[14px] w-[14px] cursor-grab touch-none rotate-45 rounded-[2px] bg-blue-500"
                        style={{ left: x, top: (ROW - 14) / 2 }}
                      />
                    ) : (
                      <div
                        {...common}
                        title={t.title}
                        className={`absolute cursor-grab touch-none rounded-sm ${STATUS[t.status]?.bar ?? 'bg-gray-300'} ${t.id === selectedId ? 'ring-1 ring-blue-500' : ''}`}
                        style={{ left: x, width: w, top: (ROW - BAR_H) / 2, height: BAR_H }}
                      >
                        {t.progress ? (
                          <div
                            className="absolute inset-y-0 left-0 rounded-l-sm"
                            style={{
                              width: `${Math.min(100, t.progress)}%`,
                              backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0 3px, rgba(0,0,0,0.08) 3px 6px)',
                            }}
                          />
                        ) : null}
                        <span className="absolute left-0 -top-px">
                          <Avatar people={people} uid={t.ownerUid} size={16} />
                        </span>
                        {w >= 90 && (
                          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] leading-none text-gray-800">
                            {days} day{days === 1 ? '' : 's'}
                          </span>
                        )}
                        <span data-edge="1" className="absolute inset-y-0 right-0 w-2 cursor-ew-resize" />
                      </div>
                    )}
                    {live && (
                      <span
                        className="pointer-events-none absolute whitespace-nowrap rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-white"
                        style={{ left: x + w + 6, top: ROW / 2 - 9 }}
                      >
                        {md(sp.s)} - {md(sp.e)}
                      </span>
                    )}
                  </div>
                );
              })}

              <svg className="pointer-events-none absolute left-0 top-0 z-[5]" width={chartW} height={bodyH}>
                {arrows.map((a) => (
                  <g key={a.key} className="text-gray-400">
                    <path d={a.d} fill="none" stroke="currentColor" strokeWidth={1} />
                    <polygon points={a.tip} fill="currentColor" />
                  </g>
                ))}
              </svg>

              {!hasDates && (
                <div className="pointer-events-none absolute left-0 top-10 z-30 w-[560px] text-center text-sm text-gray-400">
                  Add start and target dates to tasks to see them here.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
